import { guidedEmptySuggestedLabel } from './growthEngineRecommendation'
import { postGuidedSuccessInteraction } from './growthEngineSuggestedData'

export const RECIPES_WALKTHROUGH_FEATURE_ID = 'recipes'

const asStepId = value => String(value || '').trim()

const stepIdsFrom = steps =>
  (Array.isArray(steps) ? steps : [])
    .map(step => asStepId(step?.id))
    .filter(Boolean)

const knownCompletedStepIds = (stepIds, completedStepIds) => {
  const allowed = new Set(stepIds)
  const seen = new Set()
  const next = []
  for (const raw of Array.isArray(completedStepIds) ? completedStepIds : []) {
    const id = asStepId(raw)
    if (!allowed.has(id) || seen.has(id)) continue
    seen.add(id)
    next.push(id)
  }
  return next
}

const nextIncompleteStepId = (stepIds, completedStepIds) => {
  const done = new Set(completedStepIds)
  return (
    stepIds.find(id => !done.has(id)) || stepIds[stepIds.length - 1] || null
  )
}

const toIso = (value, fallback) => {
  if (value) {
    const date = new Date(value)
    if (!Number.isNaN(date.getTime())) return date.toISOString()
  }
  return fallback ? new Date(fallback).toISOString() : null
}

export function isWalkthroughInCoolOff(progress, now = new Date()) {
  if (!progress?.cooledUntil) return false
  return new Date(now).getTime() < new Date(progress.cooledUntil).getTime()
}

/**
 * Omitted list = allow (stepper tests / unknown). Empty list = no product
 * to cost, so Next must not walk a fake Step 1–3 journey.
 */
export function recipesWalkthroughHasSuggestedProduct(suggestedItems) {
  if (suggestedItems == null) return true
  if (!Array.isArray(suggestedItems)) return false
  return suggestedItems.some(item => {
    if (guidedEmptySuggestedLabel(item)) return true
    return Boolean(String(item?.productId || '').trim())
  })
}

/**
 * 0 chips: hide Step 1–3 (empty why stays). With a chip, Next / steps stay.
 */
export function getRecipesWalkthroughProductGate(suggestedItems) {
  const hasProduct = recipesWalkthroughHasSuggestedProduct(suggestedItems)
  return {
    hasProduct,
    allowNext: hasProduct,
    showStepChrome: hasProduct,
    showWalkthrough: hasProduct,
  }
}

/** Recipes only — other features stay empty-state-only unless Express sent steps. */
export function shouldShowRecipesWalkthrough(guidedSuccess, now = new Date()) {
  if (guidedSuccess?.featureId !== RECIPES_WALKTHROUGH_FEATURE_ID) return false
  const steps = guidedSuccess?.walkthrough?.steps
  if (!Array.isArray(steps) || !steps.length) return false
  const progress = guidedSuccess.walkthrough.progress
  if (progress?.status === 'completed') return false
  if (isWalkthroughInCoolOff(progress, now)) return false
  return true
}

export function recipesWalkthroughNeedsResume(progress) {
  if (!progress) return false
  if (progress.status === 'skipped') return true
  if (progress.status !== 'in_progress') return false
  return (
    Array.isArray(progress.completedStepIds) &&
    progress.completedStepIds.length > 0
  )
}

/** Full create form is the first win — do not stack a second walkthrough on it. */
export function shouldHideRecipesWalkthroughOnCreateModal(createModalOpen) {
  return Boolean(createModalOpen)
}

/**
 * Apply start / step / skip / resume / complete using Express steps.
 * Do not invent a journey from featureId.
 */
export function applyClientWalkthroughTransition({
  steps = [],
  progress = {},
  interaction,
  stepId,
  now = new Date(),
} = {}) {
  const stepIds = stepIdsFrom(steps)
  if (!stepIds.length) return { ok: false, reason: 'no_walkthrough' }

  const completedStepIds = knownCompletedStepIds(
    stepIds,
    progress.completedStepIds,
  )
  const startedAt = toIso(progress.startedAt, null)

  if (interaction === 'walkthrough_step_completed') {
    const id = asStepId(stepId)
    if (!stepIds.includes(id)) return { ok: false, reason: 'unknown_step' }
    const nextCompleted = completedStepIds.includes(id)
      ? completedStepIds
      : [...completedStepIds, id]
    const allDone = stepIds.every(step => nextCompleted.includes(step))
    const currentStepId = nextIncompleteStepId(stepIds, nextCompleted)
    return {
      ok: true,
      progress: {
        status: allDone ? 'completed' : 'in_progress',
        currentStepId,
        resumeStepId: allDone ? null : currentStepId,
        completedStepIds: nextCompleted,
        startedAt: startedAt || now.toISOString(),
        skippedAt: null,
        cooledUntil: null,
      },
    }
  }

  if (interaction === 'walkthrough_skipped') {
    const currentStepId = nextIncompleteStepId(stepIds, completedStepIds)
    return {
      ok: true,
      progress: {
        status: 'skipped',
        currentStepId,
        resumeStepId: null,
        completedStepIds,
        startedAt,
        skippedAt: now.toISOString(),
        cooledUntil: null,
      },
    }
  }

  if (interaction === 'walkthrough_completed') {
    return {
      ok: true,
      progress: {
        status: 'completed',
        currentStepId: stepIds[stepIds.length - 1],
        resumeStepId: null,
        completedStepIds: [...stepIds],
        startedAt: startedAt || now.toISOString(),
        skippedAt: null,
        cooledUntil: null,
      },
    }
  }

  if (
    interaction === 'walkthrough_started' ||
    interaction === 'walkthrough_resumed'
  ) {
    const currentStepId = nextIncompleteStepId(stepIds, completedStepIds)
    return {
      ok: true,
      progress: {
        status: 'in_progress',
        currentStepId,
        resumeStepId: currentStepId,
        completedStepIds,
        startedAt: startedAt || now.toISOString(),
        skippedAt: null,
        cooledUntil: null,
      },
    }
  }

  return { ok: false, reason: 'invalid_interaction' }
}

export function currentRecipesWalkthroughStep(walkthrough) {
  const steps = Array.isArray(walkthrough?.steps) ? walkthrough.steps : []
  if (!steps.length) return null
  const currentId =
    walkthrough?.progress?.resumeStepId ||
    walkthrough?.progress?.currentStepId ||
    steps[0].id
  return steps.find(step => step.id === currentId) || steps[0]
}

export function postRecipesWalkthroughInteraction(
  venueId,
  interaction,
  metadata = {},
) {
  return postGuidedSuccessInteraction(venueId, {
    featureId: RECIPES_WALKTHROUGH_FEATURE_ID,
    interaction,
    metadata,
  })
}
